import React, { useEffect, useState } from 'react';
import { BookOpen, Archive, MapPin, FileText, Tag } from 'lucide-react';
import C from '../../constants/colors';
import * as kbApi from '../../api/kb';

export default function KBBrowserView({ sites, zones }) {
  const [entries, setEntries] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [siteFilter, setSiteFilter] = useState('');
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const data = await kbApi.list({
        siteId: siteFilter || undefined,
        archived: showArchived ? 'true' : undefined,
      });
      setEntries(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, [siteFilter, showArchived]);

  async function handleArchive(id, archived) {
    try {
      if (archived) await kbApi.unarchive(id);
      else await kbApi.archive(id);
      await refresh();
    } catch (e) { alert('Failed: ' + e.message); }
  }

  return (
    <div style={{ padding: 24, color: C.textPrimary }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <BookOpen size={22} color={C.blue} />
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Knowledge Base</h2>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <select value={siteFilter} onChange={e => setSiteFilter(e.target.value)}
          style={{ padding: '6px 10px', background: C.surface, border: `1px solid ${C.border}`,
                   borderRadius: 6, color: C.textPrimary, fontSize: 12 }}>
          <option value="">All sites</option>
          {(sites || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <label style={{ fontSize: 12, color: C.textSecondary, cursor: 'pointer' }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)}
            style={{ marginRight: 6 }} />
          Show archived
        </label>
      </div>

      {/* Entries list */}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: C.textMuted }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ padding: 24, background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 8, color: C.textMuted, fontSize: 13, textAlign: 'center' }}>
          No entries yet. Promote findings from the search view to populate.
        </div>
      ) : entries.map(e => (
        <div key={e.id} style={{ padding: 16, background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{e.title}</div>
              <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.5 }}>{e.body}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8, fontSize: 11 }}>
                {e.workType && <Chip icon={<Tag size={10} />} text={e.workType} color={C.blue} />}
                {e.equipmentRef && <Chip icon={<FileText size={10} />} text={e.equipmentRef} color={C.amber} />}
                {(e.hazards || []).map((h, i) => <Chip key={'h'+i} text={`!${h}`} color={C.red} />)}
                {(e.keywords || []).map((k, i) => <Chip key={'k'+i} text={k} color={C.textMuted} />)}
                {e.gpsLat != null && e.gpsLng != null && (
                  <Chip icon={<MapPin size={10} />}
                    text={`${e.gpsLat.toFixed(3)}, ${e.gpsLng.toFixed(3)}`} color={C.green} />
                )}
              </div>
              {e.sourceDocId && (
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 6,
                  fontFamily: 'JetBrains Mono, monospace' }}>
                  Source: {e.sourceDocId}
                </div>
              )}
            </div>
            <button onClick={() => handleArchive(e.id, e.archived)}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: e.archived ? C.green : C.textMuted, padding: 4 }}>
              <Archive size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Chip({ icon, text, color }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 8px', borderRadius: 4, background: color + '22', color }}>
      {icon} {text}
    </span>
  );
}
